import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { selectedAccessToken } from '../../../redux/reducers/accessTokenSlice';
import { type AppearanceInput } from '../BaseAvatarPanel';
import { bigFiveQuestions } from './Big5Questions';
import { FutureSelfStories, type FutureSelfStory } from './FutureSelfStories';

interface FutureSelvesSelectionProps {
  avatarImage: string;
  avatarAppearance: AppearanceInput | null;
  bigFiveAnswers: Record<string, number>;
  selectedValues: string[];
  selectedInterests: string[];
  onComplete: (selectedStory: FutureSelfStory) => void | Promise<void>;
  completionError?: string;
  isCompletingSelection?: boolean;
}

export function FutureSelvesSelection({
  avatarImage,
  avatarAppearance,
  bigFiveAnswers,
  selectedValues,
  selectedInterests,
  onComplete,
  completionError = '',
  isCompletingSelection = false,
}: FutureSelvesSelectionProps) {
  const accessToken = useAppSelector(selectedAccessToken);
  const [futureSelves, setFutureSelves] = useState<FutureSelfStory[]>([]);
  const [isGeneratingFutureSelves, setIsGeneratingFutureSelves] = useState(false);
  const [futureSelvesError, setFutureSelvesError] = useState('');
  const [selectedFutureSelfIndex, setSelectedFutureSelfIndex] = useState<number | null>(null);
  const authHeaders = {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };

  const normalizeStory = (story: FutureSelfStory) => {
    const rawStory = story as FutureSelfStory & {
      hook_title?: string;
      hook_subtitle?: string;
      illustrationPrompt?: string;
      illustrationPromptHmac?: string;
    };
    return {
      ...story,
      hookTitle: rawStory.hook_title ?? story.hookTitle,
      hookSubtitle: rawStory.hook_subtitle ?? story.hookSubtitle,
      illustrationPrompt: rawStory.illustrationPrompt,
      illustrationPromptHmac: rawStory.illustrationPromptHmac,
    };
  };

  useEffect(() => {
    const generateFutureSelves = async () => {
      setIsGeneratingFutureSelves(true);
      setFutureSelvesError('');
      setFutureSelves([]);
      setSelectedFutureSelfIndex(null);

      const bigFiveByTrait = bigFiveQuestions.reduce<Record<string, number>>((result, question) => {
        const answer = bigFiveAnswers[question.id];
        if (answer !== undefined) {
          result[question.trait] = answer;
        }
        return result;
      }, {});

      const fetchTextCompletion = async (): Promise<string> => {
        const response = await fetch(`${import.meta.env.VITE_BFF_URL}/genai/teame223/future-self/stories`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            bigFiveAnswers: bigFiveByTrait,
            selectedValues,
            selectedInterests,
            appearance: avatarAppearance,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed future self story generation: ${response.status}`);
        }

        return response.text();
      };

      const generateFutureSelfImage = async (story: FutureSelfStory): Promise<string> => {
        if (!avatarAppearance) {
          return '';
        }

        const illustrationPrompt =
          (story as FutureSelfStory & { illustrationPrompt?: string }).illustrationPrompt?.trim() ?? '';

        if (!illustrationPrompt) {
          return '';
        }

        const illustrationPromptHmac =
          (story as FutureSelfStory & { illustrationPromptHmac?: string }).illustrationPromptHmac?.trim() ?? '';

        const imageResponse = await fetch(`${import.meta.env.VITE_BFF_URL}/genai/teame223/future-self/image`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            appearance: avatarAppearance,
            illustrationPrompt,
            illustrationPromptHmac,
          }),
        });

        if (!imageResponse.ok) {
          throw new Error(`Failed to generate future self image: ${imageResponse.status}`);
        }

        const imageBlob = await imageResponse.blob();
        return URL.createObjectURL(imageBlob);
      };

      try {
        const parsedStories = JSON.parse(await fetchTextCompletion());
        const storiesPayload = Array.isArray(parsedStories) ? parsedStories : parsedStories?.result?.response;
        if (!Array.isArray(storiesPayload)) {
          throw new Error('Stories response was not an array.');
        }

        const stories = (storiesPayload as FutureSelfStory[]).map(normalizeStory);

        if (!avatarAppearance) {
          setFutureSelves(stories);
          return;
        }

        const storiesWithRenderedPrompts = await Promise.all(
          stories.map(async story => {
            try {
              const generatedImageUrl = await generateFutureSelfImage(story);

              return {
                ...story,
                generatedImageUrl,
              };
            } catch (renderError) {
              console.error(renderError);
              return {
                ...story,
                generatedImageUrl: '',
              };
            }
          }),
        );

        setFutureSelves(storiesWithRenderedPrompts);
      } catch (error) {
        console.error(error);
        setFutureSelvesError('Unable to generate future selves right now. Please try again.');
      } finally {
        setIsGeneratingFutureSelves(false);
      }
    };

    generateFutureSelves();
  }, [accessToken, avatarAppearance, avatarImage, bigFiveAnswers, selectedValues, selectedInterests]);

  return (
    <FutureSelfStories
      stories={futureSelves}
      isGenerating={isGeneratingFutureSelves}
      generationError={futureSelvesError || completionError}
      selectedStoryIndex={selectedFutureSelfIndex}
      onSelectStory={index => setSelectedFutureSelfIndex(index)}
      onStoryNext={() => undefined}
      onComplete={() => {
        if (selectedFutureSelfIndex === null) {
          return;
        }

        const selectedStory = futureSelves[selectedFutureSelfIndex];
        if (!selectedStory) {
          return;
        }

        onComplete(selectedStory);
      }}
      isCompleting={isCompletingSelection}
    />
  );
}
